import { useLayoutEffect } from 'react';
import { PickPoints } from 'config/analysisConfig';
import { BranchInfo } from 'utils/FunctionUtilites';
import {
  MarkerLinesMoveRender,
  handleNodeChange,
} from '../Renderers/vtkManagers';
import { scheduleRender } from '../renderState';

export function useCprInitialMarkerReset({
  resetref,
  info,
  analysisType,
  branchId,
  pool,
  config,
  updateCPRState,
  setData,
  setresetref,
  aorticStartIndex,
}: {
  resetref: boolean;
  info: PickPoints[];
  analysisType: string;
  branchId: number;
  pool: any;
  config: any;
  updateCPRState: (params: any) => void;
  setData: (params: any) => void;
  setresetref: (value: boolean | ((prev: boolean) => boolean)) => void;
  aorticStartIndex: number;
}) {
  useLayoutEffect(() => {
    if (!resetref) return;

    if (
      analysisType === 'plaque' ||
      analysisType === 'stenosis' ||
      analysisType === 'isr'
    ) {
      if (!info || info.length === 0) return;
      let p = info.map((point) => point.index);
      if (isNaN(p[1]) || isNaN(p[2])) {
        p[0] = config.coronary_front_expand;
        p[1] = p[0] + 50;
        p[2] = p[1] + 50;
      }
      const pick = [{ index: p[0] }, { index: p[1] }, { index: p[2] }];
      updateCPRState({
        indexedStates: {
          [branchId]: pick,
        },
      });

      MarkerLinesMoveRender(
        0,
        branchId,
        [0, 1, 2],
        [pick[0].index, pick[1].index, pick[2].index],
        pool!,
        false,
      );
      setresetref(false);
    }

    if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi') {
      const resetIndex = Number(aorticStartIndex);
      if (!Number.isFinite(resetIndex)) {
        setresetref(false);
        return;
      }

      const centerlineData = (pool?.centerData?.data as any)?.[branchId];
      const nodeData = centerlineData
        ? handleNodeChange(centerlineData, resetIndex)
        : null;
      const resetPickPoint = nodeData
        ? {
            position: nodeData.position,
            normal: nodeData.normal,
            index: resetIndex,
          }
        : { index: resetIndex };

      setData(
        analysisType === 'tavi'
          ? { tavi_current_index: resetIndex }
          : { aorta_current_index: resetIndex },
      );

      updateCPRState({
        indexedState: {
          mainIndex: {
            branchId,
            index: 0,
          },
          pickPoint: resetPickPoint,
        },
      });

      MarkerLinesMoveRender(0, branchId, [0], [resetIndex], pool!);

      // Surface3D reset is already queued by ResetView before this hook runs.
      // At that time config.aorta_current_index may still be the previous
      // marker index, so the first camera reset can use a stale normal/vector.
      // Re-run only the normal Surface3D reset after the reset index has been
      // committed to configStore and after marker/cross-section sync has been
      // queued.  Avoid custom Surface3DPanel reset branches; the existing reset
      // path keeps actor visibility/resources stable.
      if (analysisType === 'aorticValveCalcium') {
        requestAnimationFrame(() => {
          scheduleRender('Surface3D', { type: 'reset' });
        });
      }

      setresetref(false);
    }
  }, [
    resetref,
    info,
    analysisType,
    aorticStartIndex,
    branchId,
    pool,
    config,
    updateCPRState,
    setData,
    setresetref,
  ]);
}

export function useCprFaiInitialization({
  analysisType,
  branchGraphDataset,
  resetref,
  mainBranchIds,
  config,
  updateCPRState,
  mainIndex,
  pool,
  setresetref,
}: {
  analysisType: string;
  branchGraphDataset?: BranchInfo;
  resetref: boolean;
  mainBranchIds: number[];
  config: any;
  updateCPRState: (params: any) => void;
  mainIndex: number;
  pool: any;
  setresetref: (value: boolean | ((prev: boolean) => boolean)) => void;
}) {
  useLayoutEffect(() => {
    if (!(analysisType === 'fai')) return;
    if (!branchGraphDataset) return;
    if (
      mainBranchIds[0] === mainBranchIds[1] ||
      mainBranchIds[1] === mainBranchIds[2] ||
      mainBranchIds[2] === mainBranchIds[0]
    ) {
      return;
    }

    const startValue = branchGraphDataset.getFAIIndex(
      mainBranchIds[0],
      mainBranchIds[1],
      mainBranchIds[2],
    );
    const indexedStates = Object.fromEntries(
      Object.entries(startValue).map(([branch, [start, end]]) => [
        branch,
        [
          { index: start + config.coronary_front_expand },
          {
            index: Math.round((start + end) / 2) + config.coronary_front_expand,
          },
          { index: end + config.coronary_front_expand },
        ],
      ]),
    ) as Record<'RCA' | 'LAD' | 'LCX', PickPoints[]>;

    updateCPRState({
      mainIndices: mainBranchIds,
      indexedStates: {
        [mainBranchIds[0]]: indexedStates.RCA,
        [mainBranchIds[1]]: indexedStates.LAD,
        [mainBranchIds[2]]: indexedStates.LCX,
      },
    });

    let indextemp: number[] = [];
    if (mainIndex === 0) {
      indextemp = [
        indexedStates.RCA[0].index,
        indexedStates.RCA[1].index,
        indexedStates.RCA[2].index,
      ];
    } else if (mainIndex === 1) {
      indextemp = [
        indexedStates.LAD[0].index,
        indexedStates.LAD[1].index,
        indexedStates.LAD[2].index,
      ];
    } else if (mainIndex === 2) {
      indextemp = [
        indexedStates.LCX[0].index,
        indexedStates.LCX[1].index,
        indexedStates.LCX[2].index,
      ];
    }

    requestAnimationFrame(() => {
      MarkerLinesMoveRender(
        mainIndex,
        mainBranchIds[0],
        [0, 1, 2],
        indextemp,
        pool!,
        false,
      );
    });

    setresetref(false);
  }, [analysisType, branchGraphDataset, resetref]);
}
